import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useCallTypeSelectionStyles } from './CallTypeSelection.styles';
import { JsonInput } from '@shared/components/JsonInput';
import { t } from 'i18next';
import { SELECT_CALL_TYPES } from '../../constants/roomManager.constants';

type SelectCallType = (typeof SELECT_CALL_TYPES)[keyof typeof SELECT_CALL_TYPES];
type StartCallType =
  | typeof SELECT_CALL_TYPES.WEB_CALL
  | typeof SELECT_CALL_TYPES.OUTBOUND_PHONE_CALL;
interface CallTypeSelectionProps {
  onStartCall: (
    callType: StartCallType,
    phoneNumber?: string,
    attributes?: Record<string, unknown>,
  ) => void;
  agentName: string;
  setIsWSCall: (value: React.SetStateAction<boolean>) => void;
  isSquadMode?: boolean;
  disableCallButton?: boolean;
  resetToken?: number;
}

interface CallOption {
  type: SelectCallType;
  title: string;
  description: string;
  color: string;
  selectedBg: string;
  icon: string;
}

const CALL_OPTIONS: CallOption[] = [
  {
    type: SELECT_CALL_TYPES.WEB_CALL,
    title: t('callTypeSelection.webCall.title'),
    description: t('callTypeSelection.webCall.description'),
    color: '#4f6ef7',
    selectedBg: '#eff3ff',
    icon: '🌐',
  },
  {
    type: SELECT_CALL_TYPES.OUTBOUND_PHONE_CALL,
    title: t('callTypeSelection.outboundPhoneCall.title'),
    description: t('callTypeSelection.outboundPhoneCall.description'),
    color: '#e67e22',
    selectedBg: '#fef6ee',
    icon: '📞',
  },
  {
    type: SELECT_CALL_TYPES.WS,
    title: t('callTypeSelection.ws.title'),
    description: t('callTypeSelection.ws.description'),
    color: '#0ea5a0',
    selectedBg: '#eefbfa',
    icon: '⚡',
  },
];

export const CallTypeSelection = ({
  onStartCall,
  agentName: _agentName,
  setIsWSCall,
  isSquadMode: _isSquadMode = false,
  disableCallButton = false,
  resetToken = 0,
}: CallTypeSelectionProps) => {
  const { classes } = useCallTypeSelectionStyles();
  const [callType, setCallType] = useState<SelectCallType>(SELECT_CALL_TYPES.WEB_CALL);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerGender, setCustomerGender] = useState('');
  const [attributesJson, setAttributesJson] = useState('');
  const [attributesError, setAttributesError] = useState<string | null>(null);
  const [showAttributes, setShowAttributes] = useState(false);

  useEffect(() => setIsWSCall(false), []);
  useEffect(() => {
    setCallType(SELECT_CALL_TYPES.WEB_CALL);
    setPhoneNumber('');
    setPhoneError('');
    setCustomerName('');
    setCustomerGender('');
    setAttributesJson('');
    setAttributesError(null);
    setShowAttributes(false);
    setIsWSCall(false);
  }, [resetToken, setIsWSCall]);

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^\+?[0-9]{2,14}$/;
    return phoneRegex.test(phone);
  };

  const validateJson = (jsonString: string): Record<string, unknown> | null => {
    const trimmed = jsonString.trim();
    if (!trimmed) return {};

    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return null;
      }
      return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  };

  const handleStartCall = async () => {
    const attributes = validateJson(attributesJson);
    if (attributes === null) {
      setAttributesError(t('campaignPage.jsonValidation.invalidJSON'));
      return;
    }
    setAttributesError(null);

    const mergedAttributes: Record<string, unknown> = { ...attributes };
    if (customerName.trim()) mergedAttributes['name'] = customerName.trim();
    if (customerGender) mergedAttributes['gender'] = customerGender;

    const hasAttributes = Object.keys(mergedAttributes).length > 0 ? mergedAttributes : undefined;

    if (callType === SELECT_CALL_TYPES.OUTBOUND_PHONE_CALL) {
      if (!phoneNumber.trim()) {
        setPhoneError(t('callTypeSelection.phoneNumber.required'));
        return;
      }
      if (!validatePhoneNumber(phoneNumber)) {
        setPhoneError(t('callTypeSelection.phoneNumber.invalid'));
        return;
      }
      setPhoneError('');
      onStartCall(callType, phoneNumber, hasAttributes);
    } else if (callType === SELECT_CALL_TYPES.WEB_CALL) {
      onStartCall(callType, undefined, hasAttributes);
    }
  };

  const handleSelectType = (type: SelectCallType) => {
    setCallType(type);
    setIsWSCall(type === SELECT_CALL_TYPES.WS);
  };

  const getStartCallButtonText = (type: SelectCallType): string => {
    switch (type) {
      case SELECT_CALL_TYPES.OUTBOUND_PHONE_CALL:
        return t('callTypeSelection.outboundPhoneCall.button');
      case SELECT_CALL_TYPES.WEB_CALL:
        return t('callTypeSelection.webCall.button');
      default:
        return t('callTypeSelection.ws.button');
    }
  };

  const activeOption = CALL_OPTIONS.find((o) => o.type === callType);

  return (
    <Box className={classes.container}>
      <Box className={classes.formContainer}>
        <Typography className={classes.title}>{t('callTypeSelection.title')}</Typography>

        <Box className={classes.optionsContainer}>
          {CALL_OPTIONS.map((option) => {
            const isSelected = callType === option.type;
            return (
              <Box
                key={option.type}
                onClick={() => handleSelectType(option.type)}
                className={classes.optionBox}
                style={{
                  border: isSelected ? `2px solid ${option.color}` : '1.5px solid #e5e7eb',
                  backgroundColor: isSelected ? option.selectedBg : '#ffffff',
                }}>
                <Box className={classes.optionContent}>
                  <Box
                    className={classes.radioButton}
                    style={{
                      border: isSelected ? `2px solid ${option.color}` : '2px solid #d1d5db',
                      backgroundColor: isSelected ? option.color : 'transparent',
                    }}>
                    {isSelected && <Box className={classes.radioInner} />}
                  </Box>
                  <Box>
                    <Typography className={classes.optionTitle}>{option.title}</Typography>
                    <Typography className={classes.optionDescription}>
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        {callType === SELECT_CALL_TYPES.OUTBOUND_PHONE_CALL && (
          <Box className={classes.phoneInputContainer}>
            <TextField
              fullWidth
              label={t('callTypeSelection.phoneNumber.label')}
              placeholder={t('callTypeSelection.phoneNumber.placeholder')}
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
                if (phoneError) setPhoneError('');
              }}
              error={!!phoneError}
              helperText={phoneError || t('callTypeSelection.phoneNumber.helperText')}
              variant="outlined"
              size="medium"
              disabled={disableCallButton}
            />
          </Box>
        )}

        {callType !== SELECT_CALL_TYPES.WS && (
          <Box className={classes.customerFieldsContainer}>
            <TextField
              fullWidth
              size="small"
              label={t('callTypeSelection.customerName.label')}
              placeholder={t('callTypeSelection.customerName.placeholder')}
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              disabled={disableCallButton}
            />
            <FormControl fullWidth size="small" disabled={disableCallButton}>
              <InputLabel shrink>{t('callTypeSelection.customerGender.label')}</InputLabel>
              <Select
                value={customerGender}
                label={t('callTypeSelection.customerGender.label')}
                onChange={(e) => setCustomerGender(e.target.value)}
                displayEmpty
                notched>
                <MenuItem value="">
                  <em>{t('callTypeSelection.customerGender.notSelected')}</em>
                </MenuItem>
                <MenuItem value="male">{t('callTypeSelection.customerGender.male')}</MenuItem>
                <MenuItem value="female">{t('callTypeSelection.customerGender.female')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {callType !== SELECT_CALL_TYPES.WS && (
          <Box className={classes.attributesContainer}>
            <Box
              onClick={() => !disableCallButton && setShowAttributes(!showAttributes)}
              className={`${classes.attributesToggle} ${disableCallButton ? classes.attributesToggleDisabled : ''}`}>
              <Typography className={classes.attributesToggleText}>
                {showAttributes
                  ? t('callTypeSelection.attributes.hide')
                  : t('callTypeSelection.attributes.show')}
              </Typography>
              {showAttributes ? (
                <ExpandLessIcon sx={{ fontSize: 18, color: '#6b7280' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 18, color: '#6b7280' }} />
              )}
            </Box>
            <Collapse in={showAttributes}>
              <Box className={classes.attributesContent}>
                <JsonInput
                  value={attributesJson}
                  onChange={(value) => {
                    setAttributesJson(value);
                    if (attributesError) setAttributesError(null);
                  }}
                  error={attributesError}
                  rows={4}
                  disabled={disableCallButton}
                  placeholder='{"key": "value"}'
                />
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>

      {callType !== SELECT_CALL_TYPES.WS && (
        <Box className={classes.bottomSection}>
          <Button
            variant="contained"
            size="large"
            onClick={handleStartCall}
            className={classes.startButton}
            disabled={disableCallButton}
            style={{
              backgroundColor: disableCallButton ? undefined : activeOption?.color,
            }}>
            {getStartCallButtonText(callType)}
          </Button>
        </Box>
      )}
    </Box>
  );
};
